import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { Chip, ChipProps } from '../../../atomic';
import { formatDate, formatMonthAfter } from '../../../compound/pxtraCardWallet/format';
import { CardStatusRow } from '../../../../../queries/pxtraCard';

/**
 * `neutral` is the palette's grey. `default` is not: it resolves to the brand
 * indigo, which would make "not started yet" shout louder than the states
 * that actually want the manager's attention.
 */
const statusColors: Record<string, ChipProps['color']> = {
  not_activated: 'neutral',
  never_started: 'neutral',
  in_progress: 'neutral',
  awaiting_approval: 'info',
  card_pending: 'info',
  active: 'success',
  grace_period: 'warning',
  aborted: 'warning',
  request_expired: 'warning',
  blocked: 'error',
  sign_up_failed: 'error',
  issuing_failed: 'error',
  rejected: 'error',
  ended: 'error'
};

/**
 * 2.2 AK 4: for somebody without a card the reason *is* the status, so it
 * reads as one word rather than a status with an explanation under it.
 */
export const StatusCell: FC<{ row: CardStatusRow }> = ({ row }) => {
  const { t } = useTranslation();
  const key = row.not_activated_reason || row.status;
  const graceEnd = row.status === 'grace_period' ? row.grace_period_ends_at : null;

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Chip
        label={
          graceEnd
            ? t('pxtraCard:personStatus_grace_period_until', { date: formatDate(graceEnd) })
            : t(`pxtraCard:personStatus_${key}`)
        }
        color={statusColors[key] ?? 'neutral'}
        // The shared chip shouts by default; a status reads better as a word.
        sx={{ textTransform: 'none' }}
      />
      {graceEnd && row.exit_date && (
        <Typography variant="caption" color="text.secondary">
          {t('pxtraCard:gracePeriodHint', { month: formatMonthAfter(row.exit_date) })}
        </Typography>
      )}
    </Stack>
  );
};
